// ─────────────────────────────────────────────────────────────────────────────
// drawn_ui.ts — Parametrised "hand-drawn" UI animation primitives
//
// Building blocks for menus, dialogs, and interactive shapes that look like
// they were traced onto the notebook page.  All drawing is Canvas 2D — no DOM.
//
// Design: each primitive tracks its own birth time and animates procedurally.
// A ShineParams object controls intensity, cooldown speed, and glow color.
// ─────────────────────────────────────────────────────────────────────────────

// ── Shine animation parameters ───────────────────────────────────────────────

export interface ShineParams {
  /** Peak glow intensity (0–1). */
  peakIntensity: number;
  /** How quickly the glow fades after peak (seconds). */
  cooldownDuration: number;
  /** Glow color at peak. */
  glowColor: string;
  /** Secondary color for after-glow tint. */
  tintColor: string;
}

export const DEFAULT_SHINE: ShineParams = {
  peakIntensity: 1.0,
  cooldownDuration: 0.35,
  glowColor: '#ffe8a0',
  tintColor: 'rgba(200, 170, 100, 0.35)',
};

export const LACRE_SHINE: ShineParams = {
  peakIntensity: 0.9,
  cooldownDuration: 0.5,
  glowColor: '#cc3030',
  tintColor: 'rgba(180, 40, 40, 0.4)',
};

// ── Shine evaluation ─────────────────────────────────────────────────────────

/** Returns current glow alpha (0–1) given time since birth. */
export function shineAlpha(elapsed: number, shine: ShineParams): number {
  if (elapsed < 0.05) return shine.peakIntensity * (elapsed / 0.05); // quick ramp
  const fade = Math.max(0, 1 - (elapsed - 0.05) / shine.cooldownDuration);
  return shine.peakIntensity * fade;
}

// ── Frame assembly animation ─────────────────────────────────────────────────
// Phase 1: corners shine → lines trace to next corner (CW or CCW)
// Phase 2: content appears element-by-element
// Phase 3: selection rings animate

export interface FrameAnimState {
  birth: number;          // performance.now()
  clockwise: boolean;
  /** Duration of the frame-draw phase (seconds). */
  frameDuration: number;
  /** Duration of the content-reveal phase (seconds). */
  contentDuration: number;
  /** Duration of the selection-ring phase (seconds). */
  ringDuration: number;
  shine: ShineParams;
}

export function createFrameAnim(shine?: ShineParams): FrameAnimState {
  return {
    birth: performance.now(),
    clockwise: Math.random() > 0.5,
    frameDuration: 0.28,
    contentDuration: 0.22,
    ringDuration: 0.45,
    shine: shine ?? DEFAULT_SHINE,
  };
}

export interface FramePhases {
  /** 0–1: how much of the frame border is drawn. */
  frameT: number;
  /** 0–1: how much of the content is revealed. */
  contentT: number;
  /** 0–1: how much of the selection-ring phase has elapsed. */
  ringT: number;
  /** Current corner glow alpha (peaks during frame phase). */
  cornerGlow: number;
  /** Overall visibility (0 before birth, 1 when fully assembled). */
  visible: number;
}

/** Evaluate animation phases for the current time. */
export function evalFramePhases(anim: FrameAnimState, now: number): FramePhases {
  const elapsed = (now - anim.birth) / 1000;
  const total = anim.frameDuration + anim.contentDuration + anim.ringDuration;

  if (elapsed <= 0) return { frameT: 0, contentT: 0, ringT: 0, cornerGlow: 0, visible: 0 };

  const frameT = Math.min(1, elapsed / anim.frameDuration);
  const contentElapsed = Math.max(0, elapsed - anim.frameDuration);
  const contentT = Math.min(1, contentElapsed / anim.contentDuration);
  const ringElapsed = Math.max(0, elapsed - anim.frameDuration - anim.contentDuration);
  const ringT = Math.min(1, ringElapsed / anim.ringDuration);

  // Corners glow peaks at the start and when lines arrive
  const cornerGlow = shineAlpha(elapsed, anim.shine);
  const visible = Math.min(1, elapsed / total);

  return { frameT, contentT, ringT, cornerGlow, visible };
}

// ── Draw animated frame border ───────────────────────────────────────────────

/**
 * Draw an animated frame that traces from corner to corner.
 * The four sides trace sequentially (CW or CCW).
 */
export function drawAnimatedFrame(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number,
  frameT: number,
  clockwise: boolean,
  cornerGlow: number,
  shine: ShineParams,
): void {
  if (frameT <= 0) return;

  // Define corner points
  const corners = clockwise
    ? [{ x, y }, { x: x + w, y }, { x: x + w, y: y + h }, { x, y: y + h }]
    : [{ x, y }, { x, y: y + h }, { x: x + w, y: y + h }, { x: x + w, y }];

  const perimeter = 2 * (w + h);
  const drawnLen = frameT * perimeter;

  // Trace the frame
  ctx.save();
  ctx.strokeStyle = shine.tintColor;
  ctx.lineWidth = 1;
  ctx.globalAlpha = 0.6 + 0.4 * frameT;

  ctx.beginPath();
  ctx.moveTo(corners[0].x, corners[0].y);

  let remaining = drawnLen;
  for (let i = 0; i < 4 && remaining > 0; i++) {
    const from = corners[i];
    const to = corners[(i + 1) % 4];
    const segLen = Math.sqrt((to.x - from.x) ** 2 + (to.y - from.y) ** 2);
    const t = Math.min(1, remaining / segLen);
    const px = from.x + (to.x - from.x) * t;
    const py = from.y + (to.y - from.y) * t;
    ctx.lineTo(px, py);
    remaining -= segLen;
  }
  ctx.stroke();

  // Corner glow dots
  if (cornerGlow > 0.01) {
    for (const c of corners) {
      ctx.globalAlpha = cornerGlow;
      ctx.fillStyle = shine.glowColor;
      ctx.shadowColor = shine.glowColor;
      ctx.shadowBlur = 8 * cornerGlow;
      ctx.beginPath();
      ctx.arc(c.x, c.y, 2.5, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.shadowBlur = 0;
  }

  ctx.restore();
}

// ── Draw animated selection ring (lacre) ─────────────────────────────────────

export interface SelectionRingAnim {
  cx: number;
  cy: number;
  radius: number;
  /** Random start offset (0–2π). */
  startAngle: number;
  /** Ring draw speed multiplier (randomised per ring). */
  speedMul: number;
  birth: number;
  shine: ShineParams;
  /** If true, ring stays visible after animation completes. */
  persistent?: boolean;
  /** If true, skip the tracing animation and show full ring immediately. */
  circled?: boolean;
}

export function createSelectionRing(
  cx: number, cy: number, radius: number,
  delayMs = 0,
  shine?: ShineParams,
  persistent = false,
  circled = false,
): SelectionRingAnim {
  return {
    cx, cy, radius,
    startAngle: Math.random() * Math.PI * 2,
    speedMul: 0.7 + Math.random() * 0.6,
    birth: performance.now() + delayMs,
    shine: shine ?? LACRE_SHINE,
    persistent,
    circled,
  };
}

/**
 * Draw a single animated selection ring.
 * Returns true if the ring animation is still active.
 */
export function drawSelectionRing(
  ctx: CanvasRenderingContext2D,
  ring: SelectionRingAnim,
  now: number,
): boolean {
  const elapsed = (now - ring.birth) / 1000;
  if (elapsed < 0) return true; // waiting for delay

  const totalDur = 0.6 * ring.speedMul;

  // If circled (not tapped), show the full ring immediately — no tracing animation
  const t = ring.circled ? 1 : Math.min(1, elapsed / totalDur);
  // Ease-out for the "settling" feel
  const eased = t < 0.7 ? t / 0.7 : 0.7 + 0.3 * easeOutCubic((t - 0.7) / 0.3);
  const arcLen = eased * Math.PI * 2;

  if (arcLen < 0.01) return true;

  const alpha = ring.circled
    ? 0.6
    : Math.min(1, elapsed * 5) * (t < 0.95 ? 0.65 : 0.65 * (1 - (t - 0.95) / 0.05 * 0.3));

  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = ring.shine.glowColor;
  ctx.lineWidth = 1.5;
  if (elapsed < 0.2 && !ring.circled) {
    ctx.shadowColor = ring.shine.glowColor;
    ctx.shadowBlur = 6;
  }
  ctx.beginPath();
  ctx.arc(ring.cx, ring.cy, ring.radius, ring.startAngle, ring.startAngle + arcLen);
  ctx.stroke();
  ctx.shadowBlur = 0;
  ctx.restore();

  // Persistent rings stay alive forever, non-persistent die after animation
  if (ring.persistent) return true;
  return elapsed < totalDur + 0.3;
}

// ── Color palettes ───────────────────────────────────────────────────────────

export const SECTION_PALETTE = [
  '#b08060', // warm leather
  '#8a6848', // sienna
  '#6a9a88', // sage teal
  '#7888aa', // slate blue
  '#a06880', // rose
  '#c09850', // amber
  '#8a9a68', // olive
  '#9a7890', // mauve
];

export const PAGE_PALETTE = [
  '#c9a96e', // parchment gold
  '#a0897a', // warm grey
  '#b08060', // terracotta
  '#8aa0a0', // sage
  '#c0a080', // sand
  '#90a070', // moss
  '#a08090', // dusty rose
  '#80a0b0', // sky
];

// ── Tool icons (simple text placeholders for now) ────────────────────────────

export interface ToolDef {
  id: string;
  label: string;
  icon: string;   // single character or small glyph
}

export const TOOL_DEFS: ToolDef[] = [
  { id: 'ink',        label: 'Pen',        icon: '✒' },
  { id: 'nib',        label: 'Nib',        icon: '◢' },
  { id: 'pencil',     label: 'Pencil',     icon: '✏' },
  { id: 'graphite',   label: 'Graphite',   icon: '▰' },
  { id: 'watercolor', label: 'Watercolor', icon: '🖌' },
  { id: 'marker',     label: 'Marker',     icon: '🖊' },
  { id: 'chalk',      label: 'Chalk',      icon: '▪' },
  { id: 'infill',     label: 'Fill',       icon: '◧' },
  { id: 'eraser',     label: 'Eraser',     icon: '▯' },
];

export const THICKNESS_OPTIONS = [
  { label: '0.5mm', size: 0.75, radius: 1.5 },
  { label: '1mm',   size: 1.5,  radius: 2.5 },
  { label: '2mm',   size: 3,    radius: 4 },
  { label: '3mm',   size: 4.5,  radius: 5.5 },
  { label: '5mm',   size: 7,    radius: 7 },
  { label: '8mm',   size: 10,   radius: 9 },
];

export const INK_COLORS = [
  '#2c1810',  // sepia (default)
  '#1a1a2e',  // midnight
  '#7a2020',  // crimson
  '#1a6848',  // emerald
  '#2a4a8a',  // cobalt
  '#6a4010',  // umber
  '#9a3060',  // magenta
  '#1a7a7a',  // teal
  '#5a7a20',  // chartreuse
  '#b06820',  // amber
  '#505050',  // graphite
  '#aaaaaa',  // silver
];

// ── Wonky circle — reusable animated hand-drawn selection ring ───────────────
//
// A cached, randomly-generated circle path with wavy/wobbly edges.
// Can be drawn with animation (trace-on) or fully static.
// Used for color indicators, popup selection rings, etc.

export interface WonkyCircleData {
  /** Pre-computed path points (cx-relative). */
  points: Array<{ x: number; y: number }>;
  /** Total arc-length of the path (for animation). */
  totalLen: number;
  /** Random seed that produced this shape. */
  seed: number;
}

const WONKY_CIRCLE_CACHE = new Map<string, WonkyCircleData>();

/**
 * Generate (or retrieve from cache) a wonky circle path.
 * `segments` controls how many sample points around the circle.
 * `wobble` controls the amplitude of randomised distortion (fraction of radius).
 */
export function getWonkyCircle(
  radius: number,
  seed: number,
  segments = 32,
  wobble = 0.12,
): WonkyCircleData {
  const key = `${radius}:${seed}:${segments}:${wobble}`;
  const cached = WONKY_CIRCLE_CACHE.get(key);
  if (cached) return cached;

  const rng = mulberry32(seed);
  const points: Array<{ x: number; y: number }> = [];
  // Generate per-vertex radial offsets
  const offsets: number[] = [];
  for (let i = 0; i < segments; i++) {
    offsets.push((rng() - 0.5) * 2 * wobble * radius);
  }
  // Smooth the offsets so the circle is wavy, not jagged
  const smoothed: number[] = [];
  for (let i = 0; i < segments; i++) {
    const prev = offsets[(i - 1 + segments) % segments];
    const cur = offsets[i];
    const next = offsets[(i + 1) % segments];
    smoothed.push(prev * 0.25 + cur * 0.5 + next * 0.25);
  }

  let totalLen = 0;
  for (let i = 0; i <= segments; i++) {
    const idx = i % segments;
    const angle = (idx / segments) * Math.PI * 2;
    const r = radius + smoothed[idx];
    const x = Math.cos(angle) * r;
    const y = Math.sin(angle) * r;
    if (i > 0) {
      const prev = points[i - 1];
      totalLen += Math.sqrt((x - prev.x) ** 2 + (y - prev.y) ** 2);
    }
    points.push({ x, y });
  }

  const data: WonkyCircleData = { points, totalLen, seed };
  WONKY_CIRCLE_CACHE.set(key, data);
  return data;
}

/**
 * Draw a wonky circle.
 * `t` = 0..1 controls how much of the circle is drawn (for animation).
 * When `t >= 1`, the full circle is rendered.
 */
export function drawWonkyCircle(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  data: WonkyCircleData,
  t: number,
  color: string,
  lineWidth = 1.5,
  alpha = 0.7,
): void {
  if (t <= 0 || data.points.length < 2) return;
  const drawLen = Math.min(1, t) * data.totalLen;

  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth;
  ctx.beginPath();
  ctx.moveTo(cx + data.points[0].x, cy + data.points[0].y);

  let accum = 0;
  for (let i = 1; i < data.points.length; i++) {
    const px = data.points[i - 1];
    const pt = data.points[i];
    const segLen = Math.sqrt((pt.x - px.x) ** 2 + (pt.y - px.y) ** 2);
    if (accum + segLen > drawLen) {
      const frac = (drawLen - accum) / segLen;
      const ex = px.x + (pt.x - px.x) * frac;
      const ey = px.y + (pt.y - px.y) * frac;
      ctx.lineTo(cx + ex, cy + ey);
      break;
    }
    accum += segLen;
    ctx.lineTo(cx + pt.x, cy + pt.y);
  }
  ctx.stroke();
  ctx.restore();
}

// ── Utility ──────────────────────────────────────────────────────────────────

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

/** Seeded 32-bit RNG (Mulberry32) for reproducible randomness. */
function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s += 0x6d2b79f5;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t ^= t + Math.imul(t ^ (t >>> 7), 61 | t);
    return ((t ^ (t >>> 14)) >>> 0) / 0xffffffff;
  };
}
